import {
  Component,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions, GridReadyEvent, ValueGetterParams } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { myCustomTheme } from '../../constants/grid-themes';
import { ReportTemplate, ReportResult, ScheduleResponse, SCHEDULE_STATUS } from '@models/report-template.model';
import {
  selectReportTemplates,
  selectReportResults,
  selectFilteredSchedules,
  selectShowInactiveSchedules,
  selectIsRunning,
} from '@store/selectors/reporting/reporting.selectors';
import { selectHasAdminRights } from '@store/selectors/user/user.selectors';
import { ReportingActions } from '@store/actions/reporting/reporting.actions';
import { selectOrgId as selectEmrOrgId } from '@store/selectors/organization-emr-details/organization-emr-details.selector';
import { ClinicianDetailsActions } from '@store/actions/clinician-details/clinician-details.actions';
import { RunReportModalComponent } from '../modals/run-report-modal/run-report-modal.component';
import { ScheduleReportModalComponent } from '../modals/schedule-report-modal/schedule-report-modal.component';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { ReportingActionsRendererComponent } from './reporting-actions-renderer.component';
import { OrgReportsActionsRendererComponent } from './org-reports-actions-renderer.component';
import { ScheduledReportsActionsRendererComponent } from './scheduled-reports-actions-renderer.component';

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-reporting',
  standalone: true,
  imports: [CommonModule, AgGridAngular, MatCheckboxModule, MatProgressSpinnerModule],
  templateUrl: './reporting.component.html',
  styleUrl: './reporting.component.scss',
})
export class ReportingComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly destroy$ = new Subject<void>();

  theme = myCustomTheme;

  private templatesGridApi: any;
  private orgReportsGridApi: any;
  private schedulesGridApi: any;

  templates: ReportTemplate[] = [];
  reportResults: ReportResult[] = [];
  schedules: ScheduleResponse[] = [];
  isAdmin = false;
  readonly showInactiveSchedules$ = this.store.select(selectShowInactiveSchedules);
  readonly isRunning$ = this.store.select(selectIsRunning);

  // Run Reports grid
  readonly templatesColDefs: ColDef[] = [
    {
      field: 'title',
      headerName: 'Report Title',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      minWidth: 160,
      pinned: 'right',
      filter: false,
      sortable: false,
      resizable: false,
      cellRenderer: ReportingActionsRendererComponent,
    },
  ];

  // Organizational Reports grid
  readonly orgReportsColDefs: ColDef[] = [
    {
      headerName: 'Report',
      flex: 2,
      minWidth: 160,
      valueGetter: (params: ValueGetterParams) => {
        const row = params.data as ReportResult;
        if (!row) return '';
        const runDate = row.runDate
          ? new Date(row.runDate.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US')
          : '';
        return [row.reportName, row.cadence, runDate].filter(Boolean).join(' - ');
      },
    },
    {
      field: 'scheduleName',
      headerName: 'Schedule Name',
      flex: 2,
      minWidth: 160,
    },
    {
      field: 'providerId',
      headerName: 'Provider',
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        const result = params.data as ReportResult;
        if (!result) return '';
        return result.providerName ?? '—';
      },
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'runDate',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 130,
      valueFormatter: (params) =>
        params.value ? new Date(params.value.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US') : '',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      minWidth: 160,
      pinned: 'right',
      filter: false,
      sortable: false,
      resizable: false,
      cellRenderer: OrgReportsActionsRendererComponent,
    },
  ];

  // Scheduled Reports grid
  readonly schedulesColDefs: ColDef[] = [
    {
      field: 'scheduleName',
      headerName: 'Report Title',
      flex: 2,
      minWidth: 200,
    },
    {
      field: 'reportName',
      headerName: 'Report',
      flex: 1,
      minWidth: 140,
    },
    {
      headerName: 'Providers',
      flex: 1,
      minWidth: 120,
      valueGetter: (params: ValueGetterParams) => {
        const row = params.data as ScheduleResponse;
        return row?.providerId === 'Organization' || !row?.providerId ? 'Organization' : (row.providerName ?? row.providerId);
      },
    },
    {
      field: 'cadence',
      headerName: 'Schedule',
      flex: 1,
      minWidth: 130,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      flex: 1,
      minWidth: 110,
      valueFormatter: (params) =>
        params.value ? new Date(params.value + 'T00:00:00').toLocaleDateString('en-US') : '',
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      flex: 1,
      minWidth: 110,
      valueFormatter: (params) =>
        params.value ? new Date(params.value + 'T00:00:00').toLocaleDateString('en-US') : '',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      minWidth: 120,
      pinned: 'right',
      filter: false,
      sortable: false,
      resizable: false,
      cellRenderer: ScheduledReportsActionsRendererComponent,
    },
  ];

  readonly defaultColDef: ColDef = {
    flex: 1,
    filter: true,
    resizable: true,
    sortable: true,
    editable: false,
    minWidth: 100,
  };

  readonly sharedGridOptions: GridOptions = {
    rowSelection: 'single',
    pagination: true,
    paginationPageSize: 20,
    suppressClickEdit: true,
    loading: false,
  };

  readonly schedulesGridOptions: GridOptions = {
    ...this.sharedGridOptions,
    getRowStyle: (params: any) => {
      const schedule = params.data as ScheduleResponse;
      if (schedule && schedule.statusId !== SCHEDULE_STATUS.ACTIVE) {
        return { color: '#999', fontStyle: 'italic' };
      }
      return undefined;
    },
  };

  ngOnInit(): void {
    this.store.select(selectEmrOrgId).pipe(
      filter((orgId) => !!orgId),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.store.dispatch(ClinicianDetailsActions.getOrgProviders());
      this.store.dispatch(ReportingActions.loadReportResults());
      this.store.dispatch(ReportingActions.loadSchedules());
    });

    this.store.select(selectHasAdminRights)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAdmin) => {
        this.isAdmin = isAdmin;
        if (this.orgReportsGridApi) {
          this.orgReportsGridApi.setGridOption('context', { isAdmin });
          this.orgReportsGridApi.refreshCells({ force: true });
        }
      });

    this.store.select(selectReportTemplates)
      .pipe(takeUntil(this.destroy$))
      .subscribe((templates) => {
        this.templates = templates ?? [];
        this.templatesGridApi?.setGridOption('rowData', this.templates);
      });

    this.store.select(selectReportResults)
      .pipe(takeUntil(this.destroy$))
      .subscribe((results) => {
        this.reportResults = results ?? [];
        this.orgReportsGridApi?.setGridOption('rowData', this.reportResults);
      });

    this.store.select(selectFilteredSchedules)
      .pipe(takeUntil(this.destroy$))
      .subscribe((schedules) => {
        this.schedules = schedules ?? [];
        this.schedulesGridApi?.setGridOption('rowData', this.schedules);
      });

  }

  onTemplatesGridReady(params: GridReadyEvent): void {
    this.templatesGridApi = params.api;
    this.templatesGridApi.setGridOption('rowData', this.templates);
  }

  onOrgReportsGridReady(params: GridReadyEvent): void {
    this.orgReportsGridApi = params.api;
    this.orgReportsGridApi.setGridOption('context', { isAdmin: this.isAdmin });
    this.orgReportsGridApi.setGridOption('rowData', this.reportResults);
  }

  onSchedulesGridReady(params: GridReadyEvent): void {
    this.schedulesGridApi = params.api;
    this.schedulesGridApi.setGridOption('rowData', this.schedules);
  }

  onTemplatesCellClicked(params: any): void {
    if (params.column.colId !== 'actions') return;
    const action = params.event.target.dataset.action;
    const template = params.data as ReportTemplate;
    if (action === 'view') {
      this.store.dispatch(ReportingActions.viewReportTemplate());
    } else if (action === 'run') {
      this.dialog.open(RunReportModalComponent, {
        width: '540px',
        data: { template },
      });
    } else if (action === 'schedule') {
      this.dialog.open(ScheduleReportModalComponent, {
        width: '760px',
        maxHeight: '95vh',
        data: { template },
      });
    }
  }

  onOrgReportsCellClicked(params: any): void {
    if (params.column.colId !== 'actions') return;
    const action = params.event.target.dataset.action;
    const result = params.data as ReportResult;
    if (action === 'view') {
      this.store.dispatch(ReportingActions.viewReportResult({ id: result.id }));
    } else if (action === 'download') {
      this.store.dispatch(ReportingActions.downloadReportResult({ id: result.id }));
    } else if (action === 'delete' && this.isAdmin) {
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        width: '450px',
        data: {
          title: 'Delete Report',
          content: 'Are you sure you want to delete this report? This action cannot be undone.',
          confirmText: 'Delete',
          cancelText: 'Cancel',
        },
      });
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(ReportingActions.deleteReportResult({ id: result.id }));
        }
      });
    }
  }

  onSchedulesCellClicked(params: any): void {
    if (params.column.colId !== 'actions') return;
    const action = params.event.target.dataset.action;
    const schedule = params.data as ScheduleResponse;
    const template: ReportTemplate = {
      id: String(schedule.reportId),
      title: schedule.reportName,
    };
    if (action === 'edit') {
      this.dialog.open(ScheduleReportModalComponent, {
        width: '760px',
        maxHeight: '95vh',
        data: { template, schedule },
      });
    } else if (action === 'delete') {
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        width: '450px',
        data: {
          title: 'Deactivate Schedule',
          content: 'Are you sure you want to deactivate this schedule? You can reactivate it later.',
          confirmText: 'Deactivate',
          cancelText: 'Cancel',
        },
      });
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(ReportingActions.deleteSchedule({ id: schedule.id }));
        }
      });
    }
  }

  onShowInactiveSchedulesChange(isChecked: boolean): void {
    this.store.dispatch(ReportingActions.setShowInactiveSchedules({ showInactive: isChecked }));
  }

  ngOnDestroy(): void {
    this.store.dispatch(ReportingActions.resetRunningState());
    this.destroy$.next();
    this.destroy$.complete();
  }
}
